import React from "react";
import { Button, CardContent, CircularProgress, Grid, makeStyles } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { Field, formValueSelector, reduxForm } from "redux-form";
import FormDialog from "../../../custom/FormDialog";
import CustomInput from "../../../custom/CustomInput";
import CustomSelect from "../../../custom/CustomSelect";
import { country as TAX_RESIDENCES } from "../Terms";
import { NORMALIZER_BY_DOCUMENT_TYPE, TAX_DOCUMENT_TYPES, getTaxDocumentTypes } from "../../../util/taxDocuments";
import { validateDocumentId, validateDocumentType, validateEmail, validateLocale, validateName, validateTaxResidence, validateType } from "../../../util/validations";
import { compose } from "redux";
import { PERSON_TYPES, phoneNormalizer, USER_LOCALES } from "../../../util";

const trimValue = (value) => (typeof value === "string" ? value.trim() : value);

export const normalizeInvitePayload = (values) => {
  const payload = {
    name: trimValue(values.name),
    email: trimValue(values.email)?.toLowerCase(),
    locale: values.locale,
    phone: trimValue(values.phone),
    taxResidence: trimValue(values.taxResidence),
    personType: values.personType,
    documentType: values.documentType,
    documentId: trimValue(values.documentId),
  };

  return Object.keys(payload).reduce((normalized, key) => {
    if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
      normalized[key] = payload[key];
    }
    return normalized;
  }, {});
};

const validate = (values) => {
  const errors = {};
  const validationValues = { ...values, type: values.personType };

  validateName({ values: validationValues, errors });
  validateEmail({ values: validationValues, errors });
  validateLocale({ values: validationValues, errors });

  if (values.documentType) {
    validateTaxResidence({ values: validationValues, errors });
    validateType({ values: validationValues, errors });
  }

  if (values.documentId) {
    validateTaxResidence({ values: validationValues, errors });
    validateDocumentType({ values: validationValues, errors });
    validateDocumentId({ values: validationValues, errors });
  }

  return errors;
};

const useStyles = makeStyles((theme) => ({
  actions: {
    textAlign: "right",
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  loadingContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const NewInviteDialog = ({ open, onClose, handleSubmit, submitting, personType, documentType, change }) => {
  const classes = useStyles();
  const [taxResidence, setTaxResidence] = React.useState(null);
  const documentTypeValues = taxResidence ? getTaxDocumentTypes(taxResidence, personType) : [TAX_DOCUMENT_TYPES.SELECT];
  const documentNormalizer = NORMALIZER_BY_DOCUMENT_TYPE[documentType];

  const onChangePersonType = (type) => {
    change("personType", type);
    change("documentType", null);
    change("documentId", null);
  };

  const onChangeTaxResidence = (nextTaxResidence) => {
    setTaxResidence(nextTaxResidence)
    change("taxResidence", nextTaxResidence);
    change("documentType", null);
    change("documentId", null);
  };

  const onChangeDocumentType = (nextDocumentType) => {
    change("documentType", nextDocumentType);
    change("documentId", null);
  };

  return (
    <FormDialog
      title="app.invite.create"
      variant="secondary"
      open={open}
      onClose={onClose}
      noHeader
    >
      <form onSubmit={handleSubmit}>
        <CardContent>
          <Grid
            container
            spacing={1}
          >
            <Grid item xs={12} md={6}>
              <Field
                name="name"
                label="app.fullName"
                component={CustomInput}
                maxLength={100}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="email"
                label="app.email"
                component={CustomInput}
                type="email"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="locale"
                label="app.language"
                component={CustomSelect}
                values={USER_LOCALES}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="phone"
                label="app.phone"
                component={CustomInput}
                normalize={phoneNormalizer}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="personType"
                label="app.personType"
                component={CustomSelect}
                values={PERSON_TYPES}
                onChange={(e) => onChangePersonType(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="taxResidence"
                label="app.taxResidence"
                component={CustomSelect}
                values={TAX_RESIDENCES}
                onChange={(e) => onChangeTaxResidence(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="documentType"
                label="app.documentType"
                component={CustomSelect}
                values={documentTypeValues}
                disabled={!taxResidence || !personType}
                onChange={(e) => onChangeDocumentType(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="documentId"
                label="app.documentId"
                component={CustomInput}
                normalize={documentNormalizer}
                disabled={!documentType}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} className={classes.actions}>
              <Button
                onClick={onClose}
                disabled={submitting}
                className={classes.cancelButton}
              >
                <FormattedMessage id="app.cancel" />
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={submitting}
              >
                {submitting ? (
                  <span className={classes.loadingContent}>
                    <CircularProgress size={18} color="inherit" />
                    <FormattedMessage id="app.invite.creating" />
                  </span>
                ) : (
                  <FormattedMessage id="app.invite.create" />
                )}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </form>
    </FormDialog>
  );
};

const selector = formValueSelector("newInviteForm");

const mapStateToProps = (state) => {
  const currentUser = state.user.user.me ?? {};

  return {
    initialValues: {
      locale: currentUser?.locale ?? "en",
      // taxResidence: currentUser?.taxResidence ?? null,
    },
    // taxResidence: selector(state, "taxResidence"),
    personType: selector(state, "personType"),
    documentType: selector(state, "documentType"),
  };
};

export default compose(
  connect(mapStateToProps),
  reduxForm({
    form: "newInviteForm",
    validate,
    enableReinitialize: false,
  }),
)(NewInviteDialog);
